/**
 * Penelope in the trade ticket (the Penelope tab of BotModeSwitch): the surface
 * every trader has. BASIC by default — a lean, four knobs, live or practice,
 * one Run button, and one sentence about what she is doing. ADVANCED (every
 * knob, presets, kill, the research panel) sits behind a disclosure that only a
 * member whose manager has not switched Advanced off can open. Live is the
 * default whenever the box allows it and the member is not locked to practice.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Scale } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { usePenelope, type PenelopeConfig } from '@/hooks/usePenelope';
import { usePenelopeAccess, usePenelopeAdvancedAccess } from '@/hooks/usePenelopeAccess';
import type { GameRow } from '@/views/ValueDashboard/types';
import { PenelopeSettings } from '@/views/Penelope/PenelopeSettings';
import { PENELOPE_PRESETS, PENELOPE_START } from './penelopePresets';
import { penelopeStatusSentence } from './penelopeStatus';

export { PENELOPE_START };

const signed = (n: number): string => (n > 0 ? `+${n}` : `${n}`);
const LIVE_CONFIRM = 'Run Penelope LIVE on this game? She will place REAL post-only orders on Kalshi as you, against your limits.';
const GO_LIVE_CONFIRM = 'Switch Penelope to LIVE for this game? She will place REAL post-only orders on Kalshi with these settings. Her simulated orders are dropped and she starts again live.';
const PRACTICE_CONFIRM = 'Back to practice? Her resting Kalshi orders are cancelled and she simulates from here.';

export function PenelopeTicket({ game }: { game: GameRow }) {
  const access = usePenelopeAccess();
  const advanced = usePenelopeAdvancedAccess();
  const p = usePenelope(game, access);
  const [draft, setDraft] = useState<Partial<PenelopeConfig>>(PENELOPE_START);
  /** The trader's own pick; 'auto' follows what the box and their lock allow. */
  const [modeChoice, setModeChoice] = useState<'auto' | 'live' | 'shadow'>('auto');
  if (!access) return null;

  const row = p.game;
  const cfg = row?.config ?? null;
  const live = row?.live?.baseline ?? null;
  const isLive = cfg?.mode === 'live';
  const real = row?.live ? !row.live.shadow : false;
  const armed = cfg?.enabled === true;
  const draftMode: 'live' | 'shadow' = !p.liveEligible ? 'shadow' : modeChoice === 'auto' ? 'live' : modeChoice;
  const practiceOnly = p.liveEligible ? null : p.liveReason ?? 'live orders are not available here';
  const primary = 'w-full rounded py-1.5 text-sm font-bold disabled:opacity-50';
  const small = 'rounded border px-2 py-1 text-[10px] font-bold uppercase tracking-wide disabled:opacity-50';
  const slots = !p.slots ? null
    : p.slots.mine >= p.slots.maxMine ? `you have ${p.slots.mine} of your ${p.slots.maxMine} games armed on this box — stop one to run another`
      : p.slots.maxArmed - p.slots.running <= 2 ? `${p.slots.running} of ${p.slots.maxArmed} game slots on this box in use` : null;

  const advancedBlock = (settings: ReactNode, extra?: ReactNode) =>
    advanced ? (
      <details className="border-border/60 border-t pt-1">
        <summary className="text-muted-foreground cursor-pointer text-[10px] uppercase tracking-wide">Advanced settings — every knob, variants, kill</summary>
        <div className="mt-1 flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-1">
            <span className="text-muted-foreground text-[10px]">preset</span>
            {PENELOPE_PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                disabled={p.busy}
                onClick={() => (row ? void p.patch({ ...preset.config, mode: undefined } as Partial<PenelopeConfig>) : setDraft((d) => ({ ...d, ...preset.config })))}
                className="border-border/60 text-muted-foreground hover:text-foreground rounded border px-1.5 py-px font-mono text-[10px]"
                title={preset.hint}
              >
                {preset.label}
              </button>
            ))}
          </div>
          {settings}
          {extra}
        </div>
      </details>
    ) : (
      <p className="text-muted-foreground text-[10px]">Other settings use the desk's defaults.</p>
    );

  return (
    <div className="space-y-3 p-3">
      <div className="flex items-center gap-2">
        <Scale className="text-muted-foreground h-4 w-4" />
        <div className="text-foreground text-sm font-semibold">
          Penelope · {game.awayCode} @ {game.homeCode}
        </div>
        {row && (
          <span className={cn('rounded border px-1.5 py-px font-mono text-[10px] font-bold uppercase', isLive ? 'border-red-500 bg-red-500/15 text-red-500' : 'border-sky-500 text-sky-500')}>
            {isLive ? 'live' : 'practice'}
          </span>
        )}
      </div>

      {!row && (
        <>
          <p className="text-muted-foreground text-[11px]">
            Rests bids on both teams from Pinnacle's price and holds through its pulls. Runs under your account and your limits.
          </p>
          {p.lastStopped && (
            <p role="alert" className="rounded border border-red-500/60 bg-red-500/10 px-2 py-1 text-[11px] text-red-500">
              Stopped {new Date(p.lastStopped.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              {p.lastStopped.mode === 'shadow' ? ' (practice)' : ''}: {p.lastStopped.stopReason ?? 'no reason recorded'}. Run again below.
            </p>
          )}
          {p.eligible ? (
            <>
              <PenelopeSettings
                cfg={draft as PenelopeConfig}
                editable={!p.busy}
                save={(patch) => setDraft((d) => ({ ...d, ...patch }))}
                teamA={game.awayCode}
                teamB={game.homeCode}
                tier="basic"
              />
              <div className="flex items-center gap-2 text-[11px]">
                <span className="text-muted-foreground">orders</span>
                <div role="radiogroup" aria-label="live or practice" className="flex overflow-hidden rounded border">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={draftMode === 'live'}
                    disabled={p.busy || !p.liveEligible}
                    onClick={() => setModeChoice('live')}
                    className={cn('px-2 py-0.5 font-mono text-[10px] font-bold uppercase disabled:opacity-40', draftMode === 'live' ? 'bg-red-500/15 text-red-500' : 'text-muted-foreground')}
                  >
                    live
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={draftMode === 'shadow'}
                    disabled={p.busy}
                    onClick={() => setModeChoice('shadow')}
                    className={cn('px-2 py-0.5 font-mono text-[10px] font-bold uppercase', draftMode === 'shadow' ? 'bg-sky-500/15 text-sky-500' : 'text-muted-foreground')}
                  >
                    practice
                  </button>
                </div>
                {practiceOnly && <span className="text-muted-foreground text-[10px]">practice only: {practiceOnly}</span>}
              </div>
              <button
                type="button"
                disabled={p.busy}
                onClick={() => {
                  if (draftMode === 'live' && !window.confirm(LIVE_CONFIRM)) return;
                  void p.run({ ...draft, mode: draftMode });
                }}
                className={cn(primary, draftMode === 'live' ? 'bg-red-600 text-white' : 'bg-primary text-primary-foreground')}
              >
                {p.busy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : draftMode === 'live' ? 'Run Penelope live on this game' : 'Run Penelope in practice'}
              </button>
              {slots && <p className="text-[10px] text-amber-500">{slots}</p>}
              {advancedBlock(
                <PenelopeSettings cfg={draft as PenelopeConfig} editable={!p.busy} save={(patch) => setDraft((d) => ({ ...d, ...patch }))} teamA={game.awayCode} teamB={game.homeCode} tier="advanced-only" />,
              )}
            </>
          ) : (
            <p className="text-muted-foreground text-[11px]">
              {p.reasons.length > 0 ? `Not available on this game: ${p.reasons.join('; ')}` : p.error ? '' : 'Checking this game…'}
            </p>
          )}
          {p.eligible && !p.serviceRunning && (
            <p className="text-[11px] text-amber-500">The Penelope service is off on this box, so she will not run until it is turned on.</p>
          )}
        </>
      )}

      {row && cfg && (
        <>
          <p className={cn('text-[11px]', live?.regime === 'HOLD' || live?.regime === 'PULL' ? 'text-sky-500' : live?.regime === 'PAUSED' || row.parked ? 'text-amber-500' : 'text-foreground')} data-testid="penelope-status">
            {penelopeStatusSentence({ armed, parked: row.parked, practice: !isLive, teamA: game.awayCode, teamB: game.homeCode, live })}
          </p>
          {live && (
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 font-mono text-[11px]">
              <dt className="text-muted-foreground">held</dt>
              <dd>{live.inv.A.contracts} {game.awayCode} / {live.inv.B.contracts} {game.homeCode} · net {signed(live.net)}</dd>
              <dt className="text-muted-foreground">{real ? 'P&L' : 'P&L (practice)'}</dt>
              <dd>${(live.pnlCents / 100).toFixed(2)} · {row.fillsCount} fills · {live.places} placed · {live.cancels} pulled</dd>
              <dt className="text-muted-foreground">Pinnacle</dt>
              <dd>
                {game.awayCode} {typeof live.fairC.A === 'number' ? live.fairC.A.toFixed(1) : '—'} · {game.homeCode} {typeof live.fairC.B === 'number' ? live.fairC.B.toFixed(1) : '—'}
                <span className="text-muted-foreground"> ({live.pinnacleState ?? 'no coverage'}{live.fairBlock ? `, ${live.fairBlock}` : ''})</span>
              </dd>
            </dl>
          )}

          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              disabled={p.busy}
              onClick={() => void p.setArm(!armed)}
              className={cn(small, armed ? 'border-border text-foreground' : isLive ? 'border-red-500 bg-red-500/15 text-red-500' : 'border-sky-500 bg-sky-500/15 text-sky-500')}
              title={armed ? 'Stop quoting on this game (cancels her orders, keeps her inventory)' : isLive ? 'Start: she will place REAL post-only orders on Kalshi' : 'Start in practice: simulated, never writes to Kalshi'}
            >
              {armed ? 'stop' : isLive ? 'start (live)' : 'start'}
            </button>
            <button
              type="button"
              disabled={p.busy || (!isLive && !p.liveEligible)}
              onClick={() => {
                if (isLive) { if (window.confirm(PRACTICE_CONFIRM)) void p.patch({ mode: 'shadow' }); }
                else if (window.confirm(GO_LIVE_CONFIRM)) void p.patch({ mode: 'live' });
              }}
              className={cn(small, isLive ? 'border-border text-muted-foreground hover:text-foreground' : 'border-red-500 text-red-500')}
              title={isLive ? 'Back to practice (asks first)' : p.liveEligible ? 'Go live (asks first)' : `practice only: ${practiceOnly}`}
            >
              {isLive ? 'practice' : 'go live'}
            </button>
            <span className="flex-1" />
            <button type="button" disabled={p.busy} onClick={() => { if (window.confirm('Remove Penelope from this game? Her resting orders are cancelled; her inventory stays yours.')) void p.remove(); }} className="text-muted-foreground hover:text-foreground text-[10px] uppercase" title="Remove Penelope from this game (asks first)">
              remove
            </button>
          </div>

          <PenelopeSettings cfg={cfg} editable busy={p.busy} save={(patch) => void p.patch(patch)} teamA={game.awayCode} teamB={game.homeCode} tier="basic" />
          <div className="text-muted-foreground text-[10px]">Settings save as you leave a field and take effect within a second.</div>
          {advancedBlock(
            <PenelopeSettings cfg={cfg} editable busy={p.busy} save={(patch) => void p.patch(patch)} teamA={game.awayCode} teamB={game.homeCode} tier="advanced-only" />,
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={p.busy}
                onClick={() => { if (window.confirm('Kill Penelope on this game? This stops the game for good.')) void p.kill(); }}
                className={cn(small, 'border-red-500 text-red-500')}
                title="Stop this game for good"
              >
                kill
              </button>
              <Link to="/penelope" className="text-primary text-[11px] underline">
                Service status and the variant comparison are on the Penelope panel →
              </Link>
            </div>,
          )}
        </>
      )}

      {p.error && <p className="text-destructive text-xs">{p.error}</p>}
    </div>
  );
}
